import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AudioRecorder.scss";

type SavedRecording = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  durationSeconds: number;
  durationText: string;
  createdAt: string;
  objectUrl: string;
  uploadStatus: "idle" | "uploading" | "uploaded" | "failed";
  transcript?: string;
};

type PersistedRecordingMeta = Omit<SavedRecording, "objectUrl">;

type AudioInputDevice = {
  deviceId: string;
  label: string;
};

type ConfirmAction = "pause" | "resume" | "stop" | "delete" | "clearAll";

type ConfirmModalState = {
  action: ConfirmAction;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  recordingId?: string;
};

type PlayerState = {
  currentTime: number;
  duration: number;
};

type AudioRecorderProps = {
  /**
   * Keep this empty until the backend team gives the real API.
   * When ready, pass something like: /api/audio/transcribe
   */
  uploadUrl?: string;
  maxRecordings?: number;
  onRecordingSelected?: (recording: SavedRecording, audioFile: File) => void;
  onTranscriptReady?: (payload: {
    recording: SavedRecording;
    transcript: string;
    backendResponse?: unknown;
  }) => void;
};

const DB_NAME = "audio_recorder_db";
const DB_VERSION = 1;
const STORE_NAME = "recordings";
const META_STORAGE_KEY = "local_audio_recording_meta_v2";
const DEFAULT_MAX_RECORDINGS = 2;

function formatTime(totalSeconds: number) {
  const safeSeconds = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0;

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
}

function getBestMimeType() {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];

  for (const type of types) {
    if (!type) return "";

    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  return "";
}

function getFileExtension(mimeType: string) {
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  if (mimeType.includes("wav")) return "wav";
  return "webm";
}

function createId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getStoredRecordingMeta(): PersistedRecordingMeta[] {
  try {
    const raw = localStorage.getItem(META_STORAGE_KEY);

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read recording metadata:", error);
    return [];
  }
}

function saveRecordingMeta(recordings: SavedRecording[]) {
  const meta: PersistedRecordingMeta[] = recordings.map(
    ({ objectUrl, ...recording }) => recording
  );

  localStorage.setItem(META_STORAGE_KEY, JSON.stringify(meta));
}

function openAudioDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser."));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveBlobToDb(id: string, blob: Blob) {
  const db = await openAudioDb();

  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.put(blob, id);

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };

    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

async function getBlobFromDb(id: string): Promise<Blob | null> {
  const db = await openAudioDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => {
      db.close();
      resolve(request.result || null);
    };

    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

async function deleteBlobFromDb(id: string) {
  const db = await openAudioDb();

  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.delete(id);

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };

    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

async function getRecordingFile(recording: SavedRecording) {
  const blob = await getBlobFromDb(recording.id);

  if (!blob) {
    throw new Error("The selected recording could not be found in local storage.");
  }

  const extension = getFileExtension(recording.mimeType || blob.type);
  const safeName = recording.name.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();

  return new File([blob], `${safeName}.${extension}`, {
    type: recording.mimeType || blob.type || "audio/webm",
  });
}

function getReadableFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  uploadUrl = "",
  maxRecordings = DEFAULT_MAX_RECORDINGS,
  onRecordingSelected,
  onTranscriptReady,
}) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const fullRecordingPartsRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const recordingSecondsRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const volumeTimerRef = useRef<number | null>(null);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const objectUrlsRef = useRef<string[]>([]);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [status, setStatus] = useState("Ready to record");
  const [recordings, setRecordings] = useState<SavedRecording[]>([]);
  const [selectedRecordingId, setSelectedRecordingId] = useState("");
  const [audioInputs, setAudioInputs] = useState<AudioInputDevice[]>([]);
  const [selectedMicId, setSelectedMicId] = useState("");
  const [selectedMicLabel, setSelectedMicLabel] = useState("Default microphone");
  const [micLevel, setMicLevel] = useState(0);
  const [isMicPreviewActive, setIsMicPreviewActive] = useState(false);
  const [showMicSettings, setShowMicSettings] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(
    null
  );
  const [activePlaybackId, setActivePlaybackId] = useState<string | null>(null);
  const [playerStates, setPlayerStates] = useState<Record<string, PlayerState>>(
    {}
  );

  const selectedRecording = useMemo(
    () => recordings.find((recording) => recording.id === selectedRecordingId),
    [recordings, selectedRecordingId]
  );

  const hasLiveInput = isMicPreviewActive && micLevel >= 3;
  const hasNoMicInput = isMicPreviewActive && !hasLiveInput;

  const getAudioConstraints = (
    deviceId = selectedMicId
  ): MediaStreamConstraints => ({
    audio: deviceId
      ? {
          deviceId: { exact: deviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      : {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
  });

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    stopTimer();

    timerRef.current = window.setInterval(() => {
      recordingSecondsRef.current += 1;
      setRecordingSeconds(recordingSecondsRef.current);
    }, 1000);
  };

  const resetTimer = () => {
    stopTimer();
    recordingSecondsRef.current = 0;
    setRecordingSeconds(0);
  };

  const stopMicLevelMonitor = () => {
    if (volumeTimerRef.current) {
      window.clearInterval(volumeTimerRef.current);
      volumeTimerRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // Ignore close errors.
      });
      audioContextRef.current = null;
    }

    setMicLevel(0);
  };

  const stopPreviewStream = () => {
    previewStreamRef.current?.getTracks().forEach((track) => track.stop());
    previewStreamRef.current = null;
    setIsMicPreviewActive(false);
  };

  const startMicLevelMonitor = (stream: MediaStream) => {
    stopMicLevelMonitor();

    const audioContext = new AudioContext();

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {
        // Some browsers resume only after a user gesture.
      });
    }

    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.78;
    source.connect(analyser);

    audioContextRef.current = audioContext;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    volumeTimerRef.current = window.setInterval(() => {
      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => {
          // Ignore resume errors.
        });
      }

      analyser.getByteFrequencyData(dataArray);

      const average =
        dataArray.reduce((total, value) => total + value, 0) / dataArray.length;
      const noiseFloor = 5;
      const normalized = average <= noiseFloor ? 0 : average - noiseFloor;
      const nextLevel = Math.min(100, Math.round(normalized * 2.5));

      setMicLevel(nextLevel);
    }, 90);
  };

  const startMicPreview = async (deviceId = selectedMicId) => {
    try {
      if (isRecording || !navigator.mediaDevices?.getUserMedia) return;

      stopPreviewStream();
      stopMicLevelMonitor();

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(deviceId)
      );

      previewStreamRef.current = stream;
      setIsMicPreviewActive(true);
      startMicLevelMonitor(stream);
      setStatus("Live mic preview ready");
    } catch (error) {
      console.error("Failed to start live mic preview:", error);
      setIsMicPreviewActive(false);
      setMicLevel(0);
      setStatus("Live mic preview unavailable");
    }
  };

  const loadAudioInputDevices = async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        setStatus("Microphone device selection is not supported");
        return;
      }

      let permissionStream: MediaStream | null = null;

      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      } catch {
        // Browser may still return generic microphone labels.
      } finally {
        permissionStream?.getTracks().forEach((track) => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices
        .filter((device) => device.kind === "audioinput")
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${index + 1}`,
        }));

      setAudioInputs(microphones);

      if (!selectedMicId && microphones.length > 0) {
        setSelectedMicId(microphones[0].deviceId);
        setSelectedMicLabel(microphones[0].label);
        window.setTimeout(() => startMicPreview(microphones[0].deviceId), 150);
      }

      if (selectedMicId) {
        const selectedMic = microphones.find(
          (mic) => mic.deviceId === selectedMicId
        );

        if (selectedMic) {
          setSelectedMicLabel(selectedMic.label);
        }
      }

      if (microphones.length === 0) {
        setSelectedMicLabel("No microphone found");
        setIsMicPreviewActive(false);
      }
    } catch (error) {
      console.error("Failed to load microphones:", error);
      setStatus("Could not load microphones");
    }
  };

  const handleMicChange = (deviceId: string) => {
    setSelectedMicId(deviceId);

    const selectedMic = audioInputs.find((mic) => mic.deviceId === deviceId);
    setSelectedMicLabel(selectedMic?.label || "Selected microphone");

    if (!isRecording) {
      window.setTimeout(() => startMicPreview(deviceId), 80);
    }
  };

  const triggerSavedFlash = () => {
    setSavedFlash(true);

    window.setTimeout(() => {
      setSavedFlash(false);
    }, 2200);
  };

  const syncRecordings = (nextRecordings: SavedRecording[]) => {
    setRecordings(nextRecordings);
    saveRecordingMeta(nextRecordings);
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
  };

  const askToPauseRecording = () => {
    setConfirmModal({
      action: "pause",
      title: "Pause recording?",
      message:
        "The recording will pause now. You can resume it later without losing the current audio.",
      confirmLabel: "Pause for now",
      cancelLabel: "No, keep recording",
    });
  };

  const askToResumeRecording = () => {
    setConfirmModal({
      action: "resume",
      title: "Resume recording?",
      message: "The recorder will continue from where it was paused.",
      confirmLabel: "Yes, resume",
      cancelLabel: "No, keep paused",
    });
  };

  const askToStopRecording = () => {
    setConfirmModal({
      action: "stop",
      title: "Stop and save recording?",
      message:
        "This will save one complete recording locally. You can keep up to two saved recordings and choose which one to transcribe.",
      confirmLabel: "Yes, stop and save",
      cancelLabel: "No, continue recording",
      danger: true,
    });
  };

  const askToDeleteRecording = (id: string) => {
    setConfirmModal({
      action: "delete",
      title: "Delete this recording?",
      message:
        "This saved recording will be removed from local storage. This action cannot be undone.",
      confirmLabel: "Yes, delete",
      cancelLabel: "No, keep it",
      danger: true,
      recordingId: id,
    });
  };

  const askToClearAllRecordings = () => {
    setConfirmModal({
      action: "clearAll",
      title: "Clear all saved recordings?",
      message:
        "Both saved local recordings will be deleted. This action cannot be undone.",
      confirmLabel: "Yes, clear all",
      cancelLabel: "No, keep recordings",
      danger: true,
    });
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("Audio recording is not supported in this browser");
        return;
      }

      if (!window.MediaRecorder) {
        setStatus("MediaRecorder is not supported in this browser");
        return;
      }

      stopPreviewStream();
      stopMicLevelMonitor();
      fullRecordingPartsRef.current = [];
      setSavedFlash(false);
      setIsPaused(false);
      resetTimer();
      setStatus("Requesting microphone permission...");

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints()
      );

      mediaStreamRef.current = stream;
      startMicLevelMonitor(stream);
      setIsMicPreviewActive(true);

      const mimeType = getBestMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, {
            mimeType,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream, {
            audioBitsPerSecond: 128000,
          });

      mediaRecorderRef.current = recorder;

      recorder.onstart = () => {
        setIsRecording(true);
        setIsPaused(false);
        setStatus("Recording full session locally...");
        startTimer();
      };

      recorder.onpause = () => {
        setIsPaused(true);
        setStatus("Recording paused. Live mic level is still active.");
        stopTimer();
      };

      recorder.onresume = () => {
        setIsPaused(false);
        setStatus("Recording resumed...");
        startTimer();
      };

      recorder.ondataavailable = (event: BlobEvent) => {
        if (!event.data || event.data.size === 0) return;
        fullRecordingPartsRef.current.push(event.data);
      };

      recorder.onerror = (event) => {
        console.error("Recorder error:", event);
        setStatus("Recording error");
        setIsRecording(false);
        setIsPaused(false);
        stopTimer();
        stopMicLevelMonitor();
        setIsMicPreviewActive(false);
        window.setTimeout(() => startMicPreview(), 250);
      };

      recorder.onstop = async () => {
        stopTimer();
        stopMicLevelMonitor();
        setIsMicPreviewActive(false);

        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;

        const finalMimeType =
          recorder.mimeType || fullRecordingPartsRef.current[0]?.type || "audio/webm";
        const fullAudioBlob = new Blob(fullRecordingPartsRef.current, {
          type: finalMimeType,
        });

        if (!fullAudioBlob.size || fullRecordingPartsRef.current.length === 0) {
          setStatus("No audio was recorded");
          setIsRecording(false);
          setIsPaused(false);
          window.setTimeout(() => startMicPreview(), 250);
          return;
        }

        try {
          const createdAt = new Date();
          const id = createId();
          const objectUrl = URL.createObjectURL(fullAudioBlob);
          objectUrlsRef.current.push(objectUrl);

          const newRecording: SavedRecording = {
            id,
            name: `Recording ${createdAt.toLocaleString()}`,
            mimeType: fullAudioBlob.type || finalMimeType,
            size: fullAudioBlob.size,
            durationSeconds: recordingSecondsRef.current,
            durationText: formatTime(recordingSecondsRef.current),
            createdAt: createdAt.toISOString(),
            objectUrl,
            uploadStatus: "idle",
          };

          await saveBlobToDb(id, fullAudioBlob);

          const currentRecordings = recordings;
          const updatedRecordings = [newRecording, ...currentRecordings].slice(
            0,
            maxRecordings
          );
          const removedRecordings = [newRecording, ...currentRecordings].slice(
            maxRecordings
          );

          await Promise.all(
            removedRecordings.map(async (recording) => {
              await deleteBlobFromDb(recording.id).catch(() => {
                // Ignore stale DB cleanup errors.
              });

              if (recording.objectUrl) {
                URL.revokeObjectURL(recording.objectUrl);
              }
            })
          );

          syncRecordings(updatedRecordings);
          setSelectedRecordingId(newRecording.id);
          setStatus("Recording saved. Select it and press Transcribe & use.");
          triggerSavedFlash();
        } catch (error: any) {
          console.error(error);
          setStatus(error?.message || "Failed to save recording locally");
        }

        setIsRecording(false);
        setIsPaused(false);
        fullRecordingPartsRef.current = [];
        window.setTimeout(() => startMicPreview(), 250);
      };

      // No timeslice here. The full recording is emitted when stop() is called.
      recorder.start();
    } catch (error) {
      console.error(error);
      setStatus("Microphone permission denied or unavailable");
      setIsRecording(false);
      setIsPaused(false);
      setIsMicPreviewActive(false);
      stopTimer();
      stopMicLevelMonitor();
      window.setTimeout(() => startMicPreview(), 250);
    }
  };

  const performPauseRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder) return;

    if (recorder.state === "recording") {
      recorder.pause();
    }
  };

  const performResumeRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder) return;

    if (recorder.state === "paused") {
      recorder.resume();
    }
  };

  const performStopRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder) return;

    if (recorder.state === "recording" || recorder.state === "paused") {
      setStatus("Stopping and saving full recording...");
      recorder.stop();
    }
  };

  const stopAllSavedPlayers = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (!audio) return;
      audio.pause();
    });

    setActivePlaybackId(null);
  };

  const performDeleteRecording = async (id: string) => {
    const audio = audioRefs.current[id];

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    delete audioRefs.current[id];
    await deleteBlobFromDb(id).catch(() => {
      // Ignore stale DB cleanup errors.
    });

    const deletedRecording = recordings.find((recording) => recording.id === id);

    if (deletedRecording?.objectUrl) {
      URL.revokeObjectURL(deletedRecording.objectUrl);
    }

    const updatedRecordings = recordings.filter((item) => item.id !== id);

    syncRecordings(updatedRecordings);
    setStatus("Recording deleted");

    setPlayerStates((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (activePlaybackId === id) {
      setActivePlaybackId(null);
    }

    if (selectedRecordingId === id) {
      setSelectedRecordingId(updatedRecordings[0]?.id || "");
    }
  };

  const performClearAllRecordings = async () => {
    stopAllSavedPlayers();
    audioRefs.current = {};

    await Promise.all(
      recordings.map(async (recording) => {
        await deleteBlobFromDb(recording.id).catch(() => {
          // Ignore stale DB cleanup errors.
        });

        if (recording.objectUrl) {
          URL.revokeObjectURL(recording.objectUrl);
        }
      })
    );

    localStorage.removeItem(META_STORAGE_KEY);
    setRecordings([]);
    setSelectedRecordingId("");
    setPlayerStates({});
    setStatus("All recordings cleared");
  };

  const handlePauseFromConfirmModal = () => {
    closeConfirmModal();
    performPauseRecording();
  };

  const handleConfirmAction = () => {
    if (!confirmModal) return;

    const { action, recordingId } = confirmModal;

    closeConfirmModal();

    switch (action) {
      case "pause":
        performPauseRecording();
        break;

      case "resume":
        performResumeRecording();
        break;

      case "stop":
        performStopRecording();
        break;

      case "delete":
        if (recordingId) {
          void performDeleteRecording(recordingId);
        }
        break;

      case "clearAll":
        void performClearAllRecordings();
        break;

      default:
        break;
    }
  };

  const updatePlayerState = (id: string, nextState: Partial<PlayerState>) => {
    setPlayerStates((prev) => ({
      ...prev,
      [id]: {
        currentTime: prev[id]?.currentTime || 0,
        duration: prev[id]?.duration || 0,
        ...nextState,
      },
    }));
  };

  const handleLoadedMetadata = (id: string) => {
    const audio = audioRefs.current[id];

    if (!audio) return;

    updatePlayerState(id, {
      currentTime: audio.currentTime || 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handleTimeUpdate = (id: string) => {
    const audio = audioRefs.current[id];

    if (!audio) return;

    updatePlayerState(id, {
      currentTime: audio.currentTime || 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handlePlayerEnded = (id: string) => {
    const audio = audioRefs.current[id];

    if (audio) {
      audio.currentTime = 0;
    }

    updatePlayerState(id, { currentTime: 0 });

    if (activePlaybackId === id) {
      setActivePlaybackId(null);
    }
  };

  const handleTogglePlayback = async (id: string) => {
    const selectedAudio = audioRefs.current[id];

    if (!selectedAudio) return;

    if (activePlaybackId === id && !selectedAudio.paused) {
      selectedAudio.pause();
      setActivePlaybackId(null);
      return;
    }

    Object.entries(audioRefs.current).forEach(([recordingId, audio]) => {
      if (!audio || recordingId === id) return;
      audio.pause();
    });

    try {
      await selectedAudio.play();
      setActivePlaybackId(id);
    } catch (error) {
      console.error("Failed to play recording:", error);
      setStatus("Could not play this recording");
    }
  };

  const handleSeekRecording = (id: string, value: string) => {
    const audio = audioRefs.current[id];

    if (!audio) return;

    const nextTime = Number(value);
    audio.currentTime = nextTime;

    updatePlayerState(id, {
      currentTime: nextTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const setRecordingUploadStatus = (
    id: string,
    uploadStatus: SavedRecording["uploadStatus"],
    transcript?: string
  ) => {
    const updatedRecordings = recordings.map((recording) =>
      recording.id === id
        ? {
            ...recording,
            uploadStatus,
            transcript: transcript ?? recording.transcript,
          }
        : recording
    );

    syncRecordings(updatedRecordings);
  };

  const handleSelectRecording = (id: string) => {
    setSelectedRecordingId(id);
    setStatus("Recording selected. Ready to transcribe and use.");
  };

  const handleTranscribeAndUse = async () => {
    if (!selectedRecording) {
      setStatus("Select a saved recording first");
      return;
    }

    try {
      setRecordingUploadStatus(selectedRecording.id, "uploading");
      setStatus("Preparing multipart upload...");

      const audioFile = await getRecordingFile(selectedRecording);
      onRecordingSelected?.(selectedRecording, audioFile);

      const formData = new FormData();
      formData.append("audio", audioFile);
      formData.append("recording_id", selectedRecording.id);
      formData.append("recording_name", selectedRecording.name);
      formData.append("mime_type", selectedRecording.mimeType);
      formData.append("duration_seconds", String(selectedRecording.durationSeconds));
      formData.append("created_at", selectedRecording.createdAt);

      if (!uploadUrl) {
        const placeholderTranscript =
          "Backend transcribe API is not connected yet. Multipart FormData is prepared and ready to send once uploadUrl is provided.";

        console.log("Multipart upload payload prepared:", {
          file: audioFile,
          fields: {
            recording_id: selectedRecording.id,
            recording_name: selectedRecording.name,
            mime_type: selectedRecording.mimeType,
            duration_seconds: selectedRecording.durationSeconds,
            created_at: selectedRecording.createdAt,
          },
        });

        setRecordingUploadStatus(
          selectedRecording.id,
          "uploaded",
          placeholderTranscript
        );
        setStatus("Multipart payload prepared. Backend endpoint not connected yet.");
        onTranscriptReady?.({
          recording: selectedRecording,
          transcript: placeholderTranscript,
        });
        return;
      }

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      const backendResponse = contentType.includes("application/json")
        ? await response.json()
        : await response.text();

      const transcript =
        typeof backendResponse === "object" &&
        backendResponse !== null &&
        "transcript" in backendResponse
          ? String((backendResponse as { transcript?: string }).transcript || "")
          : typeof backendResponse === "string"
          ? backendResponse
          : "Transcription completed.";

      setRecordingUploadStatus(selectedRecording.id, "uploaded", transcript);
      setStatus("Recording uploaded and transcribed");
      onTranscriptReady?.({
        recording: selectedRecording,
        transcript,
        backendResponse,
      });
    } catch (error: any) {
      console.error(error);
      setRecordingUploadStatus(selectedRecording.id, "failed");
      setStatus(error?.message || "Failed to upload selected recording");
    }
  };

  useEffect(() => {
    let isMounted = true;

    const hydrateRecordings = async () => {
      const storedMeta = getStoredRecordingMeta().slice(0, maxRecordings);
      const hydrated: SavedRecording[] = [];

      for (const meta of storedMeta) {
        const blob = await getBlobFromDb(meta.id).catch(() => null);

        if (!blob) continue;

        const objectUrl = URL.createObjectURL(blob);
        objectUrlsRef.current.push(objectUrl);
        hydrated.push({ ...meta, objectUrl });
      }

      if (!isMounted) return;

      setRecordings(hydrated);
      setSelectedRecordingId(hydrated[0]?.id || "");
    };

    void hydrateRecordings();
    void loadAudioInputDevices();

    const handleDeviceChange = () => {
      void loadAudioInputDevices();
    };

    navigator.mediaDevices?.addEventListener?.("devicechange", handleDeviceChange);

    return () => {
      isMounted = false;
      stopTimer();
      stopMicLevelMonitor();
      stopPreviewStream();

      navigator.mediaDevices?.removeEventListener?.(
        "devicechange",
        handleDeviceChange
      );

      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());

      Object.values(audioRefs.current).forEach((audio) => {
        if (!audio) return;
        audio.pause();
      });

      audioRefs.current = {};
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="audio-recorder">
      <div
        className={`audio-recorder__card ${
          isRecording ? "audio-recorder__card--recording" : ""
        } ${isPaused ? "audio-recorder__card--paused" : ""} ${
          savedFlash ? "audio-recorder__card--saved" : ""
        }`}
      >
        <div className="audio-recorder__top">
          <div>
            <p className="audio-recorder__eyebrow">Local recorder</p>
            <h2>Audio Recorder</h2>
            <p className="audio-recorder__subtitle">
              Record one complete audio file locally, keep up to two recordings,
              select the best one, then send it as multipart FormData for
              transcription.
            </p>
          </div>

          <button
            type="button"
            className={`audio-recorder__settingsButton ${
              showMicSettings ? "audio-recorder__settingsButton--open" : ""
            }`}
            onClick={() => setShowMicSettings((prev) => !prev)}
            disabled={isRecording}
            aria-label="Open microphone settings"
            title="Microphone settings"
          >
            <span>⚙</span>
          </button>
        </div>

        <div className="audio-recorder__scrollArea">
          <div className="audio-recorder__stickyControls">
            <div className="audio-recorder__timerWrap">
              <div className="audio-recorder__timer">
                <div
                  className={`audio-recorder__recordIcon ${
                    isRecording && !isPaused
                      ? "audio-recorder__recordIcon--active"
                      : ""
                  } ${isPaused ? "audio-recorder__recordIcon--paused" : ""} ${
                    savedFlash ? "audio-recorder__recordIcon--saved" : ""
                  }`}
                >
                  {savedFlash ? "✓" : isPaused ? "Ⅱ" : ""}
                </div>

                <span>{formatTime(recordingSeconds)}</span>
                <small>{status}</small>
              </div>

              <div
                className={`audio-recorder__minimalLevel audio-recorder__minimalLevel--live ${
                  isMicPreviewActive ? "audio-recorder__minimalLevel--active" : ""
                } ${hasNoMicInput ? "audio-recorder__minimalLevel--empty" : ""}`}
              >
                <div className="audio-recorder__minimalLevelTop">
                  <span>
                    {hasLiveInput
                      ? isPaused
                        ? "Live mic input while paused"
                        : isRecording
                        ? "Live mic input while recording"
                        : "Live mic input preview active"
                      : hasNoMicInput
                      ? "No mic input detected"
                      : "Live mic preview starting"}
                  </span>
                  <strong>{isMicPreviewActive ? `${micLevel}%` : selectedMicLabel}</strong>
                </div>

                <div
                  className={`audio-recorder__liveBars ${
                    hasLiveInput
                      ? "audio-recorder__liveBars--hasInput"
                      : "audio-recorder__liveBars--idle"
                  }`}
                  aria-hidden="true"
                >
                  {Array.from({ length: 33 }).map((_, index) => {
                    const centerIndex = 16;
                    const distanceFromCenter = Math.abs(index - centerIndex);
                    const maxDistance = centerIndex;
                    const centerStrength = 1 - distanceFromCenter / maxDistance;
                    const idlePulse =
                      10 +
                      Math.sin(Date.now() / 260 + index * 0.45) * 4 +
                      centerStrength * 10;
                    const activeBoost = hasLiveInput
                      ? micLevel * centerStrength * 0.55
                      : 0;
                    const dynamicHeight = Math.max(
                      8,
                      Math.min(58, idlePulse + activeBoost)
                    );

                    return (
                      <i
                        key={index}
                        className={
                          hasLiveInput && dynamicHeight > 18
                            ? "audio-recorder__liveBar--lit"
                            : ""
                        }
                        style={{ height: `${dynamicHeight}px` }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="audio-recorder__actions audio-recorder__actions--player">
              <div className="audio-recorder__transportSpacer">
                <span>{selectedMicLabel}</span>
              </div>

              <div className="audio-recorder__transportControls">
                {!isRecording && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--record"
                    onClick={startRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--record" />
                    <span>Record</span>
                  </button>
                )}

                {isRecording && !isPaused && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--pause"
                    onClick={askToPauseRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--pause">
                      <i />
                      <i />
                    </i>
                    <span>Pause</span>
                  </button>
                )}

                {isRecording && isPaused && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--resume"
                    onClick={askToResumeRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--play" />
                    <span>Resume</span>
                  </button>
                )}

                {isRecording && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--stop"
                    onClick={askToStopRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--stop" />
                    <span>Stop</span>
                  </button>
                )}
              </div>

              <button
                type="button"
                className="audio-recorder__clearSavedButton"
                onClick={askToClearAllRecordings}
                disabled={recordings.length === 0 || isRecording}
              >
                Clear saved
              </button>
            </div>
          </div>

          <div
            className={`audio-recorder__micDrawer ${
              showMicSettings ? "audio-recorder__micDrawer--open" : ""
            }`}
          >
            <div className="audio-recorder__micHeader">
              <div>
                <strong>Microphone</strong>
                <span>Select the input device before starting a recording.</span>
              </div>

              <button
                type="button"
                className="audio-recorder__refreshMic"
                onClick={loadAudioInputDevices}
                disabled={isRecording}
              >
                Refresh
              </button>
            </div>

            <div className="audio-recorder__micGrid">
              {audioInputs.length === 0 && (
                <div className="audio-recorder__micEmpty">No microphones found.</div>
              )}

              {audioInputs.map((mic) => (
                <button
                  type="button"
                  key={mic.deviceId}
                  className={`audio-recorder__micOption ${
                    selectedMicId === mic.deviceId
                      ? "audio-recorder__micOption--active"
                      : ""
                  }`}
                  onClick={() => handleMicChange(mic.deviceId)}
                  disabled={isRecording}
                >
                  <span className="audio-recorder__micDot" />
                  <span className="audio-recorder__micName">{mic.label}</span>
                  <span className="audio-recorder__micCheck">
                    {selectedMicId === mic.deviceId ? "Selected" : "Use"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="audio-recorder__savedRegion">
            <div className="audio-recorder__listHeader audio-recorder__listHeader--saved">
              <div>
                <h3>Saved full recordings</h3>
                <span>
                  {recordings.length}/{maxRecordings} saved locally
                </span>
              </div>

              <button
                type="button"
                className="audio-recorder__primaryUseButton"
                onClick={handleTranscribeAndUse}
                disabled={!selectedRecording || isRecording || selectedRecording.uploadStatus === "uploading"}
              >
                {selectedRecording?.uploadStatus === "uploading"
                  ? "Preparing..."
                  : "Transcribe & use"}
              </button>
            </div>

            {recordings.length === 0 ? (
              <div className="audio-recorder__empty">
                <strong>No saved recordings yet</strong>
                <p>
                  Press Record, finish the session, then select the saved audio
                  you want to transcribe.
                </p>
              </div>
            ) : (
              <div className="audio-recorder__list">
                {recordings.map((recording) => {
                  const isSelected = selectedRecordingId === recording.id;
                  const isPlaying = activePlaybackId === recording.id;
                  const playerState = playerStates[recording.id] || {
                    currentTime: 0,
                    duration: recording.durationSeconds || 0,
                  };
                  const duration = playerState.duration || recording.durationSeconds || 0;
                  const progress = duration
                    ? Math.min(100, (playerState.currentTime / duration) * 100)
                    : 0;

                  return (
                    <div
                      key={recording.id}
                      className={`audio-recorder__item audio-recorder__item--modern ${
                        isPlaying ? "audio-recorder__item--playing" : ""
                      } ${isSelected ? "audio-recorder__item--selected" : ""}`}
                    >
                      <div className="audio-recorder__modernHeader">
                        <label className="audio-recorder__selectRow">
                          <input
                            type="radio"
                            name="selected-recording"
                            checked={isSelected}
                            onChange={() => handleSelectRecording(recording.id)}
                          />
                          <span>Select this recording</span>
                        </label>

                        <span className="audio-recorder__playingBadge">
                          {isSelected
                            ? "Selected"
                            : isPlaying
                            ? "Playing"
                            : recording.uploadStatus === "uploaded"
                            ? "Ready"
                            : "Saved"}
                        </span>
                      </div>

                      <div className="audio-recorder__modernTitleBlock">
                        <strong>{recording.name}</strong>
                        <div className="audio-recorder__meta audio-recorder__meta--modern">
                          <span>{recording.durationText}</span>
                          <span>{getReadableFileSize(recording.size)}</span>
                          <span>{recording.mimeType || "audio/webm"}</span>
                        </div>
                      </div>

                      <div className="audio-recorder__modernPlayer">
                        <button
                          type="button"
                          className={`audio-recorder__playButton ${
                            isPlaying ? "audio-recorder__playButton--active" : ""
                          }`}
                          onClick={() => handleTogglePlayback(recording.id)}
                          aria-label={isPlaying ? "Pause recording" : "Play recording"}
                        >
                          {isPlaying ? (
                            <span className="audio-recorder__pauseIcon">
                              <i />
                              <i />
                            </span>
                          ) : (
                            <span className="audio-recorder__playIcon" />
                          )}
                        </button>

                        <div className="audio-recorder__seekWrap">
                          <div className="audio-recorder__timeRow">
                            <span>{formatTime(playerState.currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                          </div>

                          <div className="audio-recorder__seekShell">
                            <div
                              className="audio-recorder__seekFill"
                              style={{ width: `${progress}%` }}
                            />

                            <input
                              className="audio-recorder__seekInput"
                              type="range"
                              min="0"
                              max={duration || 0}
                              step="0.01"
                              value={playerState.currentTime}
                              onChange={(event) =>
                                handleSeekRecording(recording.id, event.target.value)
                              }
                              aria-label="Seek recording"
                            />
                          </div>
                        </div>

                        <audio
                          ref={(audio) => {
                            audioRefs.current[recording.id] = audio;
                          }}
                          src={recording.objectUrl}
                          preload="metadata"
                          onLoadedMetadata={() => handleLoadedMetadata(recording.id)}
                          onTimeUpdate={() => handleTimeUpdate(recording.id)}
                          onEnded={() => handlePlayerEnded(recording.id)}
                        />
                      </div>

                      {recording.transcript && (
                        <div className="audio-recorder__transcriptPreview">
                          {recording.transcript}
                        </div>
                      )}

                      <div className="audio-recorder__itemActions audio-recorder__itemActions--modern">
                        <a
                          className="audio-recorder__download"
                          href={recording.objectUrl}
                          download={`${recording.name}.${getFileExtension(
                            recording.mimeType
                          )}`}
                        >
                          Download
                        </a>

                        <button
                          type="button"
                          className="audio-recorder__delete"
                          onClick={() => askToDeleteRecording(recording.id)}
                          disabled={isRecording}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

           
          </div>
        </div>
      </div>

      {confirmModal && (
        <div
          className="audio-recorder__modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="audio-recorder-confirm-title"
        >
          <div className="audio-recorder__confirmModal">
            <div
              className={`audio-recorder__confirmIcon ${
                confirmModal.danger ? "audio-recorder__confirmIcon--danger" : ""
              }`}
            >
              {confirmModal.danger ? "!" : "?"}
            </div>

            <div className="audio-recorder__confirmContent">
              <h3 id="audio-recorder-confirm-title">{confirmModal.title}</h3>
              <p>{confirmModal.message}</p>
            </div>

            <div className="audio-recorder__confirmActions">
              <button
                type="button"
                className="audio-recorder__confirmCancel"
                onClick={closeConfirmModal}
              >
                {confirmModal.cancelLabel || "No"}
              </button>

              {confirmModal.action === "stop" && isRecording && !isPaused && (
                <button
                  type="button"
                  className="audio-recorder__confirmPause"
                  onClick={handlePauseFromConfirmModal}
                >
                  Pause for now
                </button>
              )}

              <button
                type="button"
                className={`audio-recorder__confirmSubmit ${
                  confirmModal.danger
                    ? "audio-recorder__confirmSubmit--danger"
                    : ""
                }`}
                onClick={handleConfirmAction}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
